import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faPaperPlaneTop } from '@fortawesome/pro-regular-svg-icons/faPaperPlaneTop';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Group, Menu, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { getDocumentTypeLabel } from './document-type-label';
import { documentsSectionMessages } from './documents-section-messages';
import styles from './DocumentsSection.module.css';
import { DetailSection } from '../detail-section/DetailSection';

import type { LtlDocument } from '../../../../types/ltl';
import type { FreightDocumentType } from '../../freight-details-types';

type DocumentsSectionProps = {
  documents?: LtlDocument[];
  isDocumentsLoading?: boolean;
  pendingDocumentTypes?: FreightDocumentType[];
  onPrintDocument: (document: LtlDocument) => void;
  onDownloadDocument: (document: LtlDocument) => void;
  onShareDocument?: (document: LtlDocument) => void;
  onPrintAll?: () => void;
  onDownloadAll?: () => void;
  onShareAll?: () => void;
};

type DocumentActionsProps = {
  doc?: LtlDocument;
  disabled?: boolean;
  onPrint?: (document: LtlDocument) => void;
  onDownload?: (document: LtlDocument) => void;
  onShare?: (document: LtlDocument) => void;
};

const DocumentActions = ({ doc, disabled = false, onPrint, onDownload, onShare }: DocumentActionsProps) => {
  const intl = useIntl();

  return (
    <Group gap='xs'>
      {onShare && (
        <Button
          aria-label={intl.formatMessage(documentsSectionMessages.share)}
          data-testid={doc ? `share-document-${doc.key}` : undefined}
          disabled={disabled}
          onClick={doc ? () => onShare(doc) : undefined}
          px={4}
          size='condensed'
          variant='tertiary'
          w={28}
        >
          <FontAwesomeIcon icon={faPaperPlaneTop} />
        </Button>
      )}
      <Button
        aria-label={intl.formatMessage(documentsSectionMessages.download)}
        data-testid={doc ? `download-document-${doc.key}` : undefined}
        disabled={disabled}
        onClick={doc && onDownload ? () => onDownload(doc) : undefined}
        px={4}
        size='condensed'
        variant='tertiary'
        w={28}
      >
        <FontAwesomeIcon icon={faArrowDownToLine} />
      </Button>
      <Button
        aria-label={intl.formatMessage(documentsSectionMessages.print)}
        data-testid={doc ? `print-document-${doc.key}` : undefined}
        disabled={disabled}
        onClick={doc && onPrint ? () => onPrint(doc) : undefined}
        px={4}
        size='condensed'
        variant='tertiary'
        w={28}
      >
        <FontAwesomeIcon icon={faPrint} />
      </Button>
    </Group>
  );
};

export const DocumentsSection = ({
  documents,
  isDocumentsLoading,
  pendingDocumentTypes,
  onPrintDocument,
  onDownloadDocument,
  onShareDocument,
  onPrintAll,
  onDownloadAll,
  onShareAll,
}: DocumentsSectionProps) => {
  const intl = useIntl();
  const hasDocuments = (documents?.length ?? 0) > 0;
  const hasPending = (pendingDocumentTypes?.length ?? 0) > 0;
  const hasActions = onPrintAll != null || onDownloadAll != null || onShareAll != null;

  const actionsMenu = hasActions ? (
    <Menu position='bottom-end'>
      <Menu.Target>
        <Button data-testid='documents-section-actions' rightSection={<FontAwesomeIcon icon={faChevronDown} />} variant='tertiary'>
          {intl.formatMessage(documentsSectionMessages.actions)}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {onPrintAll && (
          <Menu.Item data-testid='documents-section-print-all' leftSection={<FontAwesomeIcon icon={faPrint} />} onClick={onPrintAll}>
            {intl.formatMessage(documentsSectionMessages.printAll)}
          </Menu.Item>
        )}
        {onDownloadAll && (
          <Menu.Item
            data-testid='documents-section-download-all'
            leftSection={<FontAwesomeIcon icon={faArrowDownToLine} />}
            onClick={onDownloadAll}
          >
            {intl.formatMessage(documentsSectionMessages.downloadAll)}
          </Menu.Item>
        )}
        {onShareAll && (
          <Menu.Item
            data-testid='documents-section-share-all'
            leftSection={<FontAwesomeIcon icon={faPaperPlaneTop} />}
            onClick={onShareAll}
          >
            {intl.formatMessage(documentsSectionMessages.shareAll)}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  ) : null;

  const renderContent = () => {
    if (isDocumentsLoading) {
      return (
        <Stack align='center' gap='xs'>
          <FontAwesomeIcon icon={faSpinner} spin />
          <Stack align='center' gap={4}>
            <Text fw={600} size='sm'>
              {intl.formatMessage(documentsSectionMessages.creatingDocuments)}
            </Text>
            <Text size='sm'>{intl.formatMessage(documentsSectionMessages.creatingDocumentsSubtext)}</Text>
          </Stack>
        </Stack>
      );
    }
    if (hasDocuments || hasPending) {
      return (
        <div className={styles.rows}>
          {documents?.map((doc) => (
            <div className={styles.row} key={doc.key}>
              <span className={styles.label}>{intl.formatMessage(getDocumentTypeLabel(doc.documentType))}</span>
              <DocumentActions doc={doc} onDownload={onDownloadDocument} onPrint={onPrintDocument} onShare={onShareDocument} />
            </div>
          ))}
          {pendingDocumentTypes?.map((docType) => (
            <div className={styles.row} key={docType}>
              <Stack gap={2}>
                <span className={styles.label}>{intl.formatMessage(getDocumentTypeLabel(docType))}</span>
                <span className={styles.pendingLabel}>{intl.formatMessage(documentsSectionMessages.availableAfterDelivery)}</span>
              </Stack>
              <DocumentActions disabled onShare={onShareDocument} />
            </div>
          ))}
        </div>
      );
    }
    return <span className={styles.label}>{intl.formatMessage(documentsSectionMessages.noDocuments)}</span>;
  };

  return (
    <DetailSection headerRight={actionsMenu} testId='documents-section' title={intl.formatMessage(documentsSectionMessages.sectionTitle)}>
      {renderContent()}
    </DetailSection>
  );
};
